import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material"
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  AssignmentReturn as ReturnIcon,
} from "@mui/icons-material"
import { useBill, useDeleteBill, usePrintBill } from "../../hooks/api/useBills"
import { LoadingSpinner, StatusChip } from "../../components/ui"
import { formatCurrency } from "../../utils/formatters"
import { format } from "date-fns"
import { useState } from "react"
import RecordPaymentModal from "../../components/bills/RecordPaymentModal"
import SaleReturnModal from "../../components/bills/SaleReturnModal"
import SaleReturnHistory from "../../components/bills/SaleReturnHistory"
import { useSaleReturns } from "../../hooks/api/useSaleReturns"
import { useSelector } from "react-redux"
import { RootState } from "../../store"
import { getCompanyUom } from "../../utils/companyUom"
import { billRemainingBalance } from "../../utils/billBalance"

const BillDetailsPage = () => {
  const user = useSelector((state: RootState) => state.auth.user)
  const uom = getCompanyUom(user?.company_uom)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false)
  const [saleReturnOpen, setSaleReturnOpen] = useState(false)

  const { data: bill, isLoading, error } = useBill(id!)
  const { data: returnsData } = useSaleReturns({ bill_id: id })
  const deleteBillMutation = useDeleteBill()
  const printBillMutation = usePrintBill()

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    navigate(`/bills/${id}/edit`)
    handleMenuClose()
  }

  const handlePrint = () => {
    if (bill) {
      printBillMutation.mutate(bill._id)
    }
    handleMenuClose()
  }


  const handleDeleteClick = () => {
    handleMenuClose()
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (bill) {
      deleteBillMutation.mutate(bill._id, {
        onSuccess: () => {
          setDeleteConfirmOpen(false)
          navigate("/bills")
        }
      })
    }
  }

  const getBillStatusVariant = (status: string): 'info' | 'danger' | 'neutral' => {
    switch (status) {
      case "active":    return "info"
      case "serviced":  return "info"
      case "cancelled": return "danger"
      default:          return "neutral"
    }
  }

  const getPaymentVariant = (status?: string): 'success' | 'warn' | 'danger' | 'neutral' => {
    switch (status) {
      case "paid":    return "success"
      case "partial": return "warn"
      case "unpaid":  return "danger"
      default:        return "neutral"
    }
  }

  const getPaymentLabel = (status?: string) => {
    switch (status) {
      case "paid":    return "Settled"
      case "partial": return "Partial"
      case "unpaid":  return "Unpaid"
      default:        return "—"
    }
  }

  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading bill details..." />
  }

  if (error || !bill) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="error">
          Bill not found or error loading bill details
        </Typography>
      </Box>
    )
  }

  const remainingBalance = billRemainingBalance(bill)


  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate("/bills")}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h4">{bill.customer_name}</Typography>
              {bill.is_sales_tax_invoice && (
                <Chip
                  icon={<ReceiptIcon />}
                  label="Sales Tax Invoice"
                  color="warning"
                  size="small"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Bill #{bill._id} • Created on {format(new Date(bill.createdAt), "dd MMM yyyy, hh:mm a")}
            </Typography>
            {bill.is_sales_tax_invoice && bill.sti_invoice_number && (
              <Typography variant="body2" color="warning.dark" fontWeight={600}>
                STI Invoice No: {bill.sti_invoice_number}
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            disabled={bill.status === "cancelled"}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={printBillMutation.isPending}
          >
            {printBillMutation.isPending ? 'Printing...' : 'Print'}
          </Button>
          {bill.status === 'serviced' && remainingBalance > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PaymentIcon />}
              onClick={() => setRecordPaymentOpen(true)}
            >
              Record Payment
            </Button>
          )}
          {bill.status === 'serviced' && remainingBalance > 0 && (
            <Button variant="outlined" startIcon={<ReturnIcon />} onClick={() => setSaleReturnOpen(true)}>
              Record Sale Return
            </Button>
          )}
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bill Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <StatusChip label={bill.status} variant={getBillStatusVariant(bill.status)} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Payment Status
                </Typography>
                <StatusChip label={getPaymentLabel(bill.payment_status)} variant={getPaymentVariant(bill.payment_status)} />
              </Grid>
              {bill.status === 'serviced' && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Settlement
                  </Typography>
                  <Typography variant="body2">
                    Cash Rs {formatCurrency(bill.paid_amount ?? 0)} + Returns Rs {formatCurrency(bill.returned_amount ?? 0)}
                    {' '}· Due Rs {formatCurrency(remainingBalance)}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Area
                </Typography>
                <Typography variant="body1">{bill.area_id?.area_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Booker
                </Typography>
                <Typography variant="body1">{bill.booker_id?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body1">{bill.customer_id?.name || 'N/A'}</Typography>
              </Grid>
              {bill.remarks && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Remarks
                  </Typography>
                  <Typography variant="body1">{bill.remarks}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bill Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Total {uom.plural}:</Typography>
              <Typography variant="body2">{bill.summary.quantity_ctn}</Typography>
            </Box>
            
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Total Pieces:</Typography>
              <Typography variant="body2">{bill.summary.quantity_pcs}</Typography>
            </Box>
            
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Total:</Typography>
              <Typography variant="body2">Rs {formatCurrency(bill.summary.discount > 0 ? bill.summary.net_amount : bill.summary.total)}</Typography>
            </Box>

            {bill.summary.discount > 0 && <>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Discount ({bill.summary.discount_percentage ?? bill.discount}%):</Typography>
                <Typography variant="body2">− Rs {formatCurrency(bill.summary.discount)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Total After Discount:</Typography>
                <Typography variant="body2">Rs {formatCurrency(bill.summary.total)}</Typography>
              </Box>
            </>}
            
            {bill.is_sales_tax_invoice ? (
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">
                  Sales Tax ({bill.summary.sales_tax_percentage ?? 18}%):
                </Typography>
                <Typography variant="body2">
                  Rs {(bill.summary.sales_tax ?? 0).toLocaleString()}
                </Typography>
              </Box>
            ) : bill.summary.advance_tax_applied !== false && (
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Advance Tax ({bill?.adv_tax}%):</Typography>
                <Typography variant="body2">Rs {bill.summary.adv_tax.toLocaleString()}</Typography>
              </Box>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h6">Net Payable:</Typography>
              <Typography variant="h6">Rs {formatCurrency(bill.summary.net_payable)}</Typography>
            </Box>
            {bill.status === 'serviced' && <>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Cash Collected:</Typography>
                <Typography variant="body2">Rs {formatCurrency(bill.paid_amount ?? 0)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Sale Returns:</Typography>
                <Typography variant="body2">Rs {formatCurrency(bill.returned_amount ?? 0)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography fontWeight={700}>Remaining Balance:</Typography>
                <Typography fontWeight={700}>Rs {formatCurrency(remainingBalance)}</Typography>
              </Box>
            </>}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bill Items
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box overflow="auto">
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e8e4dd", backgroundColor: '#f7f5f2' }}>
                    <th style={{ textAlign: "left", padding: "8px", fontSize: '0.75rem', color: '#7a766f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SKU</th>
                    <th style={{ textAlign: "left", padding: "8px", fontSize: '0.75rem', color: '#7a766f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Packing</th>
                    <th style={{ textAlign: "right", padding: "8px", fontSize: '0.75rem', color: '#7a766f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{uom.code} Qty</th>
                    <th style={{ textAlign: "right", padding: "8px", fontSize: '0.75rem', color: '#7a766f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>PCS Qty</th>
                    <th style={{ textAlign: "right", padding: "8px", fontSize: '0.75rem', color: '#7a766f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price</th>
                    <th style={{ textAlign: "right", padding: "8px", fontSize: '0.75rem', color: '#7a766f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.sku_items.map((item) => (
                    <tr key={item._id} style={{ borderBottom: "1px solid #e8e4dd" }}>
                      <td style={{ padding: "8px" }}>
                  {typeof item.sku_id === 'object' ? item.sku_id?.name || 'Unknown SKU' : 'Unknown SKU'}
                </td>
                      <td style={{ padding: "8px" }}>{typeof item.sku_id === 'object' ? item.sku_id.packing : (item.quantity_pcs / item.quantity_ctn || 0)}</td>
                      <td style={{ textAlign: "right", padding: "8px" }}>
                        {item.full_quantity_ctn ?? item.quantity_ctn}{item.loose_pcs ? ` ${uom.code} + ${item.loose_pcs} PCS` : ''}
                      </td>
                      <td style={{ textAlign: "right", padding: "8px" }}>{item.quantity_pcs}</td>
                      <td style={{ textAlign: "right", padding: "8px" }}>Rs {formatCurrency(item.price)}</td>
                      <td style={{ textAlign: "right", padding: "8px" }}>Rs {formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Grid>
        {bill.status === 'serviced' && <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Sale Return History</Typography>
            <Divider sx={{ mb: 2 }} />
            <SaleReturnHistory bill={bill} returns={returnsData?.results ?? []} />
          </Paper>
        </Grid>}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Bill
        </MenuItem>
      </Menu>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this bill? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteBillMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {recordPaymentOpen && (
        <RecordPaymentModal
          open={recordPaymentOpen}
          onClose={() => setRecordPaymentOpen(false)}
          bill={bill}
        />
      )}
      {saleReturnOpen && <SaleReturnModal open={saleReturnOpen} onClose={() => setSaleReturnOpen(false)} bill={bill} />}
    </Box>
  )
}

export default BillDetailsPage
